"use client";

import { useEffect, useState } from "react";

import { useTranslations } from "next-intl";

import ReactPaginate from "react-paginate";
import { useSelector } from "react-redux";
import Select from "react-select";

import {
  useGetTemplateCategoriesQuery,
  useLazyGetTemplateListQuery,
} from "@/store";

import { useScrollToTop } from "@/hooks";
import { Filter } from "./Filter";
import { Loading } from "./Loading";
import { NoDataForTheme } from "./NoDataForTheme";
import { SlideOvers } from "./SlideOvers";
import { ThemeCard } from "./ThemeCard";

export const SelectTheme = ({ name: site_name, lang }) => {

  useScrollToTop();

  const t = useTranslations();

  const [category, setCategory] = useState("");
  const [selectedTheme, setSelectedTheme] = useState();
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [filterIds, setFilterIds] = useState([]);
  const [template, setTemplate] = useState();
  const [isSlideOverOpen, setIsSlideOverOpen] = useState(false);

  const { search } = useSelector((state) => state.template);
  
  const [getTemplateList, { data, isSuccess }] = useLazyGetTemplateListQuery();

  const { data: categoryData } = useGetTemplateCategoriesQuery();

  const getTemplate = async () => {
    setIsLoading(true);
    const response = await getTemplateList({
      category_ids: category,
      keyword: "",
      style_ids: filterIds,
      page,
    });
    setIsLoading(false);
    setTemplate(response.data);
  };

  const handleSearch = async () => {
    setIsLoading(true);
    const response = await getTemplateList({
      category_ids: "",
      keyword: search,
      style_ids: "",
      page,
    });
    setIsLoading(false);
    setTemplate(response.data);
  };

  useEffect(() => {
    search && handleSearch();
  }, [search]);

  useEffect(() => {
    getTemplate();
  }, [category, filterIds, page]);

  const handleSlideOverOpen = (item) => {
    setSelectedTheme(item);
    setIsSlideOverOpen(true);
  };

  const handleSlideOverClose = () => {
    setIsSlideOverOpen(false);
  };

  const { meta } = template ?? {};
  const totalPages = meta?.last_page ?? 0;

  const optionsCategory = categoryData?.data?.map(({ id, name }) => ({
    value: id,
    label: name,
  }));

  return (
    <div className="lg:m-4 ">
      <h1 className="text-center lmd:mt-10 mt-4 mb-3 font-bold md:text-3xl text-xl">
        {t("selectThemeBelow")}
      </h1>
      <div className="mb-2  mx-auto text-center max-w-[90rem] pb-3 px-1 hidden xmd:block">
        {categoryData && (
          <button
            onClick={() => setCategory("")}
            type="button"
            className="md:mr-2 mr-1 inline-flex md:my-2 my-1 items-center px-4 py-2 border border-transparent md:text-base sm:text-xs text-xxs font-medium rounded-md text-white bg-mainLightPurple hover:bg-mainLightPurpleHover  focus:outline-none"
          >
            {t("all")}
          </button>
        )}
        {categoryData?.data?.map(({ id, name }) => (
          <button
            onClick={() => setCategory(id)}
            key={id}
            type="button"
            className="md:mr-2 mr-1 inline-flex md:my-2 my-1 items-center px-4 py-2 border border-transparent md:text-base sm:text-xs text-xxs font-medium rounded-md text-white bg-mainLightPurple hover:bg-mainLightPurpleHover  focus:outline-none"
          >
            {name}
          </button>
        ))}
      </div>
      <div className="mb-1  sm:px-4 mx-auto text-center w-full border-gray-100 pb-0 px-1  block xmd:hidden">
        <Select
        id="category"
          isSearchable={false}
          onChange={(selectedOption) => setCategory(selectedOption.value)}
          options={[{ value: "", label: t("all") }, ...(optionsCategory ?? [])]}
          defaultValue={{ value: "", label: t("all") }}
          className=" rounded-lg"
        />
      </div>

      <div className="border-t border-gray-100">
        <Filter setFilterIds={setFilterIds} />
      </div>
      {isLoading ? (
        <Loading />
      ) : (
        <>
          {template?.data.length > 0 ? (
            <div className="mb-24 sm:p-4 mx-auto max-w-[1296px] h-full grid sm:grid-cols-1 lmd:grid-cols-2 xl:grid-cols-3 gap-6">
              {template?.data?.map(
                ({
                  id,
                  categories,
                  description,
                  name,
                  image,
                  demo_url,
                  pages, installs_count
                }) => {
                  return (
                    <ThemeCard
                      {...{
                        id,
                        categories,
                        description,
                        name,
                        image,
                        demo_url,
                        pages, installs_count
                      }}
                      setSelectedTheme={setSelectedTheme}
                      key={id}
                      onClick={handleSlideOverOpen}
                      site_name={site_name}
                    />
                  );
                }
              )}
            </div>
          ) : template?.data.length == 0 ? (
            <NoDataForTheme
              message={t("themeNotFound")}
              description={t("themeNotFoundDesc")}
            />
          ) : (
            <Loading />
          )}
          <div className="mt-[4rem] mb-[3rem] flex items-center justify-center">
            {(totalPages > 1) & template ? (
              <ReactPaginate
                breakLabel="..."
                className="flex"
                breakClassName="text-black p-2 self-end"
                pageLinkClassName="w-full h-full grid place-items-center rounded-lg"
                activeLinkClassName="text-black bg-blue-500 text-white rounded-lg"
                pageClassName="border border-grey-200 w-10 h-10 rounded-lg text-slate-400 grid bg-white place-items-center m-1 hover:bg-blue-200 hover:text-white transition"
                nextLabel=">"
                nextClassName="border border-grey-200 w-10 h-10 rounded-lg text-slate-400 grid bg-white place-items-center m-1"
                pageCount={totalPages}
                onPageChange={(e) => setPage(e.selected + 1)}
                pageRangeDisplayed={4}
                previousLabel="<"
                renderOnZeroPageCount={null}
                previousClassName="border border-grey-200 w-10 h-10 rounded-lg text-slate-400 grid bg-white place-items-center m-1"
                previousLinkClassName="w-full h-full grid place-items-center"
                nextLinkClassName="w-full h-full grid place-items-center"
              />
            ) : null}
          </div>
        </>
      )}
      <SlideOvers
        name={site_name}
        lang={lang}
        isOpen={isSlideOverOpen}
        selectedTheme={selectedTheme}
        onClose={handleSlideOverClose}
      />
    </div>
  );
};
